// A soft ring that breathes underneath the event pin the user has picked.
// The pin itself only grows, and on a trip where a dozen events sit within
// a few metres of each other that alone is easy to miss - the ring is what
// actually draws the eye to "this is the one you clicked".
//
// Driven by requestAnimationFrame rather than an animated SVG: Google
// rasterises a marker icon once, so SMIL or CSS inside the icon's data URI
// would never run.

const PULSE_PERIOD_MS = 1500;
const PULSE_MIN_SCALE = 11;
const PULSE_MAX_SCALE = 22;
// redrawing for less than this is spending a setIcon on nothing visible
const SCALE_EPSILON = 0.25;
const PULSE_COLOR = "#1570EF";

export type SelectedEventPulse = ReturnType<typeof createSelectedEventPulse>;

export const createSelectedEventPulse = (map: any) => {
  const google = (window as any).google;
  // not the parameter directly: the fleet map's auto-refresh unmounts the
  // map component and builds a fresh google.maps.Map, and a ring still
  // holding the old one is drawn onto a map that is gone - which read as
  // the pulse simply not coming back after a refresh. See attachTo.
  let activeMap = map;
  let marker: any = null;
  let frame: number | null = null;
  let lastScale = -1;
  // the pulse is phased off this rather than off the wall clock, so moving
  // to another event restarts the ring from its smallest instead of
  // teleporting it mid-cycle - which reads as the animation not firing at
  // all when you pick a second event
  let cycleStartedAt = 0;

  const ringIcon = (scale: number) => {
    // 0 at the smallest, 1 at the widest - the ring fades as it expands so
    // it reads as a pulse going outward rather than a disc sitting there
    const spread =
      (scale - PULSE_MIN_SCALE) / (PULSE_MAX_SCALE - PULSE_MIN_SCALE);
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale,
      fillColor: PULSE_COLOR,
      fillOpacity: 0.18 * (1 - spread),
      strokeColor: PULSE_COLOR,
      strokeOpacity: 0.9 - 0.65 * spread,
      strokeWeight: 2,
    };
  };

  const step = () => {
    if (!marker) return;
    const phase =
      ((Date.now() - cycleStartedAt) % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
    const scale =
      PULSE_MIN_SCALE + (PULSE_MAX_SCALE - PULSE_MIN_SCALE) * phase;
    if (Math.abs(scale - lastScale) >= SCALE_EPSILON) {
      marker.setIcon(ringIcon(scale));
      lastScale = scale;
    }
    frame = requestAnimationFrame(step);
  };

  const showAt = (point: { latitude: number; longitude: number }) => {
    const position = { lat: point.latitude, lng: point.longitude };
    // every call is a fresh pick - start the cycle over so the ring is seen
    // to fire on the event that was just clicked
    cycleStartedAt = Date.now();
    lastScale = -1;
    if (!marker) {
      marker = new google.maps.Marker({
        position,
        map: activeMap,
        // the pin underneath has to stay the thing you click
        clickable: false,
        // a vector-symbol marker is unoptimized whatever is asked for, so
        // say so: it shares a layer with the selected event's marker, and
        // between those two the z-index holds
        optimized: false,
        // over the ordinary pins so it is not buried in a stack, under the
        // selected event's own marker so the ring reads as behind its pin
        zIndex: 390,
        icon: ringIcon(PULSE_MIN_SCALE),
      });
    } else {
      marker.setPosition(position);
      marker.setMap(activeMap);
    }
    if (frame === null) step();
  };

  const hide = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    marker?.setMap(null);
    lastScale = -1;
  };

  const destroy = () => {
    hide();
    marker = null;
  };

  // point the ring at a different google.maps.Map - for a caller that keeps
  // the pulse across a map being torn down and rebuilt. The marker itself
  // is re-parented rather than dropped, so a visible ring stays visible.
  const attachTo = (nextMap: any) => {
    if (!nextMap || nextMap === activeMap) return;
    activeMap = nextMap;
    if (marker?.getMap()) marker.setMap(activeMap);
  };

  return { showAt, hide, destroy, attachTo };
};
